import React, { Suspense } from 'react';
import { createBrowserRouter, Routes, Route } from 'react-router-dom';
import { routeConfig } from './navigation-service';
import { LoaderOverlay } from '@/components/common/LoaderOverlay';
import ErrorPageRoute from '@/pages/ErrorPage';
import { lazyWithRetry, safePreload } from '@/Utils/lazyWithRetry';

// Lazy load page components for better initial load performance
const WithdrawPage = lazyWithRetry(() => import('@/pages/WithdrawPage'), 'WithdrawPage');
const WithdrawalConfirmation = lazyWithRetry(() => import('@/pages/WithdrawalConfirmation'), 'WithdrawalConfirmation');
const ProtectedRoute = lazyWithRetry(() => import('@/components/auth/ProtectedRoute').then(m => ({ default: m.ProtectedRoute })), 'ProtectedRoute');
const AuthCallback = lazyWithRetry(() => import('@/pages/AuthCallback'), 'AuthCallback');

/**
 * Preload components in the background after initial load
 * This ensures subsequent navigation is instant
 */
export const preloadComponents = () => {
  // Preload all route components in the background
  // These will be cached by the browser for instant subsequent loads
  setTimeout(() => {
    // Main route components
    safePreload(() => import('@/pages/WithdrawPage'), 'WithdrawPage');
    safePreload(() => import('@/pages/WithdrawalConfirmation'), 'WithdrawalConfirmation');
    safePreload(() => import('@/components/auth/ProtectedRoute'), 'ProtectedRoute');
    safePreload(() => import('@/pages/AuthCallback'), 'AuthCallback');
    
    // Heavy child components within pages
    safePreload(() => import('@/pages/WithdrawalAccordion/WithdrawalAccordion'), 'WithdrawalAccordion');
    safePreload(() => import('@/pages/PreviousWithdrawals/PreviousWithdrawals'), 'PreviousWithdrawals');
    
    // Withdrawal method components
    safePreload(() => import('@/pages/WithdrawalMethods'), 'WithdrawalMethods');
    
    // Modal components
    safePreload(() => import('@/components/BankOfflineModal'), 'BankOfflineModal');
    safePreload(() => import('@/components/VoucherOfflineModal'), 'VoucherOfflineModal');
  }, 100);
};

const routeErrorElement = (
  <ErrorPageRoute
    title="We could not load withdrawals"
    message="Please close this window and try opening withdrawals again."
    buttonText="Close"
  />
);

/**
 * Application routing configuration
 * Centralizes all route definitions for better maintainability
 */
export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoaderOverlay />}>
      <Routes>
        <Route path={routeConfig.withdraw} element={<WithdrawPage />} />
        <Route path={routeConfig.withdrawIframe} element={<WithdrawPage />} />
        <Route path={routeConfig.withdrawalConfirmation} element={<WithdrawalConfirmation />} />
      </Routes>
    </Suspense>
  );
};

/**
 * Router configuration for RouterProvider
 */
export const router = createBrowserRouter([
  {
    path: routeConfig.authCallback,
    errorElement: routeErrorElement,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <AuthCallback />
      </Suspense>
    )
  },
  {
    path: routeConfig.withdraw,
    errorElement: routeErrorElement,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <WithdrawPage />
      </Suspense>
    ),
  },
  {
    path: routeConfig.withdrawIframe,
    errorElement: routeErrorElement,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <ProtectedRoute>
          <WithdrawPage />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: routeConfig.withdrawalConfirmation,
    errorElement: routeErrorElement,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <WithdrawalConfirmation />
      </Suspense>
    ),
  },
  {
    path: routeConfig.error,
    errorElement: routeErrorElement,
    element: <ErrorPageRoute />,
  },
]);

/**
 * Route metadata for additional information
 */
export const routeMetadata = {
  [routeConfig.withdraw]: {
    title: 'Withdrawals',
    description: 'Withdrawal interface',
    requiresAuth: false,
  },
  [routeConfig.withdrawIframe]: {
    title: 'Withdrawals - Iframe',
    description: 'Withdrawal interface for iframe',
    requiresAuth: false,
  },
  [routeConfig.iframe]: {
    title: 'Iframe Container',
    description: 'Iframe wrapper page',
    requiresAuth: false,
  },
  [routeConfig.withdrawalConfirmation]: {
    title: 'Withdrawal Confirmation',
    description: 'Confirm withdrawal details',
    requiresAuth: false,
  },
  [routeConfig.error]: {
    title: 'Error',
    description: 'Error page',
    requiresAuth: false,
  },
} as const;
